import React, { useEffect, useState } from 'react';
import {
    Container,
    Grid,
    Paper,
    Typography,
    Card,
    CardContent,
    Box,
    TextField,
    MenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import axios from '../../services/api';
import { 
    PeopleAlt as PeopleIcon,
    CardMembership as SubscriptionIcon,
    Description as PapersIcon,
    Quiz as QuizIcon
} from '@mui/icons-material';

interface DashboardStats {
    users: {
        total: number;
        students: number;
        content_creators: number;
        admins: number;
    };
    subscriptions: {
        active_count: number;
        total_value: number;
    };
    attempts: {
        total: number;
        passed: number;
        pass_rate: number;
    };
}

interface DailyStats {
    date: string;
    registrations: number;
    subscriptions: number;
    subscription_amount: number;
}

interface PaperStats {
    total_papers: number;
    past_papers: number;
    current_papers: number;
    future_papers: number;
}

const Dashboard: React.FC = () => {
    const { t } = useTranslation();
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
    const [paperStats, setPaperStats] = useState<PaperStats | null>(null);
    
    // Filter state
    const [themeOptions, setThemeOptions] = useState<any[]>([]);
    const [quizOptions, setQuizOptions] = useState<any[]>([]);
    const [quizStatusOptions] = useState([
        { value: 'current', label: t('admin.dashboard.filter.statusOptions.current', 'Current') },
        { value: 'past', label: t('admin.dashboard.filter.statusOptions.past', 'Past') },
        { value: 'coming_soon', label: t('quiz.comingSoon') },
    ]);
    const [selectedThemes, setSelectedThemes] = useState<number[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['current']);
    const [selectedQuizzes, setSelectedQuizzes] = useState<number[]>([]);

    // Fetch filter options on mount
    useEffect(() => {
        const fetchThemesAndPapers = async () => {
            try {
                const res = await import('../../services/modules/quiz');
                const { QuizService } = res;
                // Get token from localStorage or context if needed
                const token = localStorage.getItem('token') || '';
                // Fetch all themes
                const themes = await QuizService.getThemes(token);
                setThemeOptions(themes);
                // Fetch all papers for all themes
                let allPapers: any[] = [];
                for (const theme of themes) {
                    try {
                        const papers = await QuizService.getPapers(theme.theme_id, token);
                        if (Array.isArray(papers)) {
                            allPapers = allPapers.concat(papers);
                        }
                    } catch (err) {
                        // Ignore errors for individual theme fetch
                    }
                }
                setQuizOptions(allPapers);
            } catch (e) {
                setThemeOptions([]);
                setQuizOptions([]);
            }
        };
        fetchThemesAndPapers();
    }, []);

    // Handlers for dropdowns
    const handleThemeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSelectedThemes(Array.isArray(value) ? value.map(Number) : []);
    };
    const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSelectedStatuses(Array.isArray(value) ? value : []);
    };
    const handleQuizChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setSelectedQuizzes(Array.isArray(value) ? value.map(Number) : []);
    };

    // Initialize date filters with past month
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    
    const [startDate, setStartDate] = useState(lastMonth.toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);

    // Fetch overall stats
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await axios.get('/admin/dashboard/stats');
                setStats(response.data);
            } catch (error) {
                console.error('Error fetching dashboard stats:', error);
            }
        };

        const fetchPaperStats = async () => {
            try {
                const response = await axios.get('/admin/dashboard/paper-stats');
                setPaperStats(response.data);
            } catch (error) {
                console.error('Error fetching paper stats:', error);
            }
        };

        fetchStats();
        fetchPaperStats();
    }, []);

    // Fetch daily stats with date filter
    useEffect(() => {
        const fetchDailyStats = async () => {
            try {
                const response = await axios.get('/admin/dashboard/daily-stats', {
                    params: {
                        start_date: startDate,
                        end_date: endDate
                    }
                });
                setDailyStats(response.data);
            } catch (error) {
                console.error('Error fetching daily stats:', error);
            }
        };

        fetchDailyStats();
    }, [startDate, endDate]);

    // Pie chart state
    const theme = useTheme();
    // Use theme palette for first few colors, then fallback to HSL
    function getPieColors(n: number): string[] {
        const paletteColors = [
            theme.palette.primary.main,
            theme.palette.secondary.main,
            theme.palette.success.main,
            theme.palette.error.main,
            theme.palette.primary.light,
            theme.palette.secondary.light,
            theme.palette.success.light,
            theme.palette.error.light,
            theme.palette.primary.dark,
            theme.palette.secondary.dark,
            theme.palette.success.dark,
            theme.palette.error.dark
        ];
        if (n <= paletteColors.length) {
            return paletteColors.slice(0, n);
        }
        // Fill with palette, then HSL for overflow
        return [
            ...paletteColors,
            ...Array.from({ length: n - paletteColors.length }, (_, i) => `hsl(${Math.round((360 * i) / (n - paletteColors.length))}, 70%, 55%)`)
        ];
    }
    type CompletionPieDatum = { completed: number; count: number };
    const [completionPie, setCompletionPie] = useState<CompletionPieDatum[] | null>(null);
    const [selectedCompletedCount, setSelectedCompletedCount] = useState<string[] | 'all'>([]);

    useEffect(() => {
        const fetchCompletionPie = async () => {
            try {
                const res = await axios.get('/admin/dashboard/completion-pie');
                setCompletionPie(res.data);
            } catch (e) {
                console.error('Error fetching completion pie:', e);
            }
        };
        fetchCompletionPie();
    }, []);

    // Set default selection ["0","1","2","3","4","5","6"] or up to max available if less than 7
    useEffect(() => {
        if (completionPie && completionPie.length > 0 && (selectedCompletedCount === undefined || (Array.isArray(selectedCompletedCount) && selectedCompletedCount.length === 0))) {
            const maxCompleted = Math.max(...completionPie.map(d => d.completed));
            const defaultMax = Math.min(6, maxCompleted);
            const defaultSelection = Array.from({length: defaultMax + 1}, (_, i) => String(i));
            setSelectedCompletedCount(defaultSelection);
        }
    }, [completionPie]);

    // Filtered data for chart and legend
    const filteredCompletionPie = React.useMemo(() => {
        if (!completionPie) return [];
        if (selectedCompletedCount === 'all') return completionPie;
        if (Array.isArray(selectedCompletedCount)) {
            return completionPie.filter(d => selectedCompletedCount.includes(String(d.completed)));
        }
        return completionPie;
    }, [completionPie, selectedCompletedCount]);

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" gutterBottom>
                {t('admin.dashboard.title')}
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
                {t('admin.dashboard.overview')}
            </Typography>

            <Grid container spacing={3}>
                {/* Overview Cards */}
                {/* Total Users */}
                <Grid item xs={12} md={6} lg={3}>
                    <Card 
                        sx={{ 
                            height: '100%', 
                            display: 'flex', 
                            flexDirection: 'column',
                            bgcolor: '#2c3e50',
                            opacity: 0.9,
                            '&:hover': {
                                opacity: 1,
                                boxShadow: 6,
                            }
                        }}
                    >
                        <CardContent sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <PeopleIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                                <Typography sx={{ color: 'white' }}>
                                    {t('admin.dashboard.stats.totalUsers')}
                                </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ color: 'white' }} gutterBottom>
                                {stats?.users.total ? stats.users.total.toLocaleString() : 0}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                        {t('admin.dashboard.stats.students')}:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                        {stats?.users.students ? stats.users.students.toLocaleString() : 0}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                        {t('admin.dashboard.stats.contentCreators')}:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                        {stats?.users.content_creators ? stats.users.content_creators.toLocaleString() : 0}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                        {t('admin.dashboard.stats.administrators')}:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                        {stats?.users.admins ? stats.users.admins.toLocaleString() : 0}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Active Subscriptions */}
                <Grid item xs={12} md={6} lg={3}>
                    <Card 
                        sx={{ 
                            height: '100%', 
                            display: 'flex', 
                            flexDirection: 'column',
                            bgcolor: '#2c3e50',
                            opacity: 0.9,
                            '&:hover': {
                                opacity: 1,
                                boxShadow: 6,
                            }
                        }}
                    >
                        <CardContent sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <SubscriptionIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                                <Typography sx={{ color: 'white' }}>
                                    {t('admin.dashboard.stats.activeSubscriptions')}
                                </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ color: 'white' }} gutterBottom>
                                {stats?.subscriptions.active_count ? stats.subscriptions.active_count.toLocaleString() : 0}
                            </Typography>
                            <Box sx={{ mt: 2 }}>
                                <Typography sx={{ color: 'white' }} gutterBottom>
                                    {t('admin.dashboard.stats.totalValue')}
                                </Typography>
                                <Typography variant="h4" sx={{ color: 'white' }}>
                                    MYR {stats?.subscriptions.total_value ? stats.subscriptions.total_value.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0}) : '0'}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Question Papers Status */}
                <Grid item xs={12} md={6} lg={3}>
                    <Card 
                        sx={{ 
                            height: '100%', 
                            display: 'flex', 
                            flexDirection: 'column',
                            bgcolor: '#2c3e50',
                            opacity: 0.9,
                            '&:hover': {
                                opacity: 1,
                                boxShadow: 6,
                            }
                        }}
                    >
                        <CardContent sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <PapersIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                                <Typography sx={{ color: 'white' }}>
                                    {t('admin.dashboard.stats.questionPapersStatus')}
                                </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ color: 'white' }} gutterBottom>
                                {paperStats?.total_papers ? paperStats.total_papers.toLocaleString() : 0}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                        {t('admin.dashboard.stats.pastPapers')}:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                        {paperStats?.past_papers ? paperStats.past_papers.toLocaleString() : 0}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                        {t('admin.dashboard.stats.currentPapers')}:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                        {paperStats?.current_papers ? paperStats.current_papers.toLocaleString() : 0}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                        {t('admin.dashboard.stats.futurePapers')}:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                        {paperStats?.future_papers ? paperStats.future_papers.toLocaleString() : 0}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Total Quiz Attempts */}
                <Grid item xs={12} md={6} lg={3}>
                    <Card 
                        sx={{ 
                            height: '100%', 
                            display: 'flex', 
                            flexDirection: 'column',
                            bgcolor: '#2c3e50',
                            opacity: 0.9,
                            '&:hover': {
                                opacity: 1,
                                boxShadow: 6,
                            }
                        }}
                    >
                        <CardContent sx={{ flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <QuizIcon sx={{ fontSize: 40, color: 'white', mr: 2 }} />
                                <Typography sx={{ color: 'white' }}>
                                    {t('admin.dashboard.stats.totalAttempts')}
                                </Typography>
                            </Box>
                            <Typography variant="h4" sx={{ color: 'white' }} gutterBottom>
                                {stats?.attempts.total ? stats.attempts.total.toLocaleString() : 0}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                        {t('admin.dashboard.stats.passedAttempts')}:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                        {stats?.attempts.passed ? stats.attempts.passed.toLocaleString() : 0}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                        {t('admin.dashboard.stats.passRate')}:
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'white' }}>
                                        {stats?.attempts.pass_rate?.toFixed(1) || '0.0'}%
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Daily Stats Chart */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h6">
                                {t('admin.dashboard.dailyStats.title')}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TextField
                                    label={t('admin.dashboard.dateRangeFilter.startDate')}
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                />
                                <TextField
                                    label={t('admin.dashboard.dateRangeFilter.endDate')}
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                    size="small"
                                />
                            </Box>
                        </Box>
                        <Box sx={{ height: 400 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={dailyStats}
                                    margin={{
                                        top: 20,
                                        right: 30,
                                        left: 20,
                                        bottom: 5,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis 
    dataKey="date"
    tick={{ fontSize: 11 }}
    tickFormatter={date => {
        if (!date) return '';
        const d = new Date(date);
        if (isNaN(d.getTime())) return date;
        const day = d.getDate().toString().padStart(2, '0');
        const month = d.toLocaleString('en-US', { month: 'short' });
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    }}
/>
                                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                    <Tooltip />
                                    <Legend
                                        layout="vertical"
                                        align="right"
                                        verticalAlign="middle"
                                        wrapperStyle={{ right: 0, top: 60, width: 120, position: 'absolute', fontSize: 12 }}
                                    />
                                    <Bar 
                                        dataKey="registrations" 
                                        name={t('admin.dashboard.dailyStats.registrations')} 
                                        fill="var(--primary-color)" 
                                    />
                                    <Bar 
                                        dataKey="subscriptions" 
                                        name={t('admin.dashboard.dailyStats.subscriptions')} 
                                        fill="#82ca9d" 
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </Box>
                    </Paper>
                </Grid>

                {/* Completion Pie Chart */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, mt: 4, height: 430, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" gutterBottom>
                            {t('admin.dashboard.completionPie.title', 'Student Completion Distribution')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                            <TextField
                                select
                                SelectProps={{
                                    multiple: true,
                                    renderValue: (selected) => {
                                        const selectedArr = selected as string[];
                                        if (selectedArr.length === 0 || (selectedArr.length === 1 && selectedArr[0] === 'all')) return t('admin.dashboard.completionPie.all', 'All');
                                        return selectedArr.join(', ');
                                    },
                                }}
                                label={t('admin.dashboard.completionPie.filter', 'Number of Papers Completed')}
                                value={selectedCompletedCount === 'all' ? [] : selectedCompletedCount}
                                onChange={e => {
                                    const value = e.target.value;
                                    if (Array.isArray(value) && value.includes('all')) {
                                        setSelectedCompletedCount('all');
                                    } else if (Array.isArray(value) && value.length === 0) {
                                        setSelectedCompletedCount('all');
                                    } else {
                                        setSelectedCompletedCount(value as unknown as string[]);
                                    }
                                }}
                                size="small"
                                sx={{ minWidth: 220 }}
                            >
                                <MenuItem value="all">{t('admin.dashboard.completionPie.all', 'All')}</MenuItem>
                                {completionPie && completionPie.length > 0 && (() => {
                                    const maxCompleted = Math.max(...completionPie.map(d => d.completed));
                                    return Array.from({ length: maxCompleted + 1 }, (_, i) => (
                                        <MenuItem key={i} value={String(i)}>
                                            {i}
                                        </MenuItem>
                                    ));
                                })()}
                            </TextField>
                        </Box>
                        <Box sx={{ flex: 1, height: '100%' }}>
                            {filteredCompletionPie && filteredCompletionPie.length > 0 && (
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        {/* Generate as many colors as segments */}
                                        {(() => {
                                            const pieColors = getPieColors(filteredCompletionPie.length);
                                            return (
                                                <Pie
                                                    data={filteredCompletionPie.map((d, i) => ({
                                                        name: `${d.completed} ${t('admin.dashboard.completionPie.papers', d.completed === 1 ? 'paper' : 'papers')}`,
                                                        value: d.count,
                                                        completed: d.completed,
                                                        fill: pieColors[i]
                                                    }))}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    cx="50%"
                                                    cy="50%"
                                                    outerRadius={120}
                                                    label={({ percent, cx, cy, midAngle, outerRadius }) => {
    // Place label outside the slice
    const RADIAN = Math.PI / 180;
    const offset = 18; // distance outside the arc
    const x = cx + (outerRadius + offset) * Math.cos(-midAngle * RADIAN);
    const y = cy + (outerRadius + offset) * Math.sin(-midAngle * RADIAN);
    return (
        <text
            x={x}
            y={y}
            fill="#333"
            textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline="central"
            fontSize={11}
        >
            {(percent * 100).toFixed(1)}%
        </text>
    );
}}
                                                >
                                                    {filteredCompletionPie.map((entry, i) => (
                                                        <Cell key={`cell-${i}`} fill={pieColors[i]} />
                                                    ))}
                                                </Pie>
                                            );
                                        })()}

                                        <Tooltip formatter={(value: any, name: any) => [`${(value)}`, name]} />
                                        <Legend 
                                            layout="vertical" 
                                            align="right" 
                                            verticalAlign="middle" 
                                            iconType="circle" 
                                            wrapperStyle={{ right: 0, top: 60, width: 180, fontSize: 12 }}
                                            formatter={(value: string) => {
                                                // Extract number from value (e.g., '2 papers')
                                                const match = value.match(/^(\d+)/);
                                                const num = match ? match[1] : value;
                                                return `${num} Papers Completed`;
                                            }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                            {(!completionPie || completionPie.length === 0) && (
                                <Typography variant="body2" color="text.secondary">
                                    {t('admin.dashboard.completionPie.loading', 'Loading completion data...')}
                                </Typography>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                {/* Sankey Chart for Email & Subscription */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, mt: 4, height: 430, display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="h6" gutterBottom>
                            {t('admin.dashboard.sankey.title', 'Student Email & Subscription Funnel')}
                        </Typography>
                        <Box sx={{ flex: 1, height: '100%' }}>
                            <SankeyChart />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Paper Attempts Bar Chart */}
            <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, mt: 4 }}>
                        <Typography variant="h6" gutterBottom>
                            {t('admin.dashboard.attemptsByPaper', 'Student Attempts & Pass/Fail by Question Paper')}
                        </Typography>
                        {/* Filters for Theme, Quiz Status, Quiz Name */}
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                            {/* Theme Filter */}
                            <TextField
                                select
                                SelectProps={{ multiple: true }}
                                label={t('admin.dashboard.filter.theme', 'Theme')}
                                value={selectedThemes}
                                onChange={handleThemeChange}
                                sx={{ minWidth: 180 }}
                                size="small"
                            >
                                {themeOptions.map((theme) => (
                                    <MenuItem key={theme.theme_id} value={theme.theme_id}>
                                        {theme.title}
                                    </MenuItem>
                                ))}
                            </TextField>
                            {/* Quiz Status Filter */}
                            <TextField
                                select
                                SelectProps={{ multiple: true }}
                                label={t('admin.dashboard.filter.status', 'Quiz Status')}
                                value={selectedStatuses}
                                onChange={handleStatusChange}
                                sx={{ minWidth: 180 }}
                                size="small"
                            >
                                {quizStatusOptions.map((status) => (
                                    <MenuItem key={status.value} value={status.value}>
                                        {t(`admin.dashboard.filter.statusOptions.${status.value}`, status.label)}
                                    </MenuItem>
                                ))}
                            </TextField>
                            {/* Quiz Name Filter */}
                            <TextField
                                select
                                SelectProps={{ multiple: true }}
                                label={t('admin.dashboard.filter.quiz', 'Quiz Name')}
                                value={selectedQuizzes}
                                onChange={handleQuizChange}
                                sx={{ minWidth: 220 }}
                                size="small"
                            >
                                {quizOptions.map((quiz) => (
                                    <MenuItem key={quiz.paper_id} value={quiz.paper_id}>
                                        {quiz.title}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                        <Box sx={{ height: 420 }}>

                            <PaperAttemptsBarChart 
                                selectedThemes={selectedThemes}
                                selectedStatuses={selectedStatuses}
                                selectedQuizzes={selectedQuizzes}
                            />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

// --- PaperAttemptsBarChart ---
import { LabelList } from 'recharts';

interface PaperAttemptsBarChartProps {
    selectedThemes: number[];
    selectedStatuses: string[];
    selectedQuizzes: number[];
}
const PaperAttemptsBarChart: React.FC<PaperAttemptsBarChartProps> = ({ selectedThemes, selectedStatuses, selectedQuizzes }) => {
    const theme = useTheme();
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        const fetchData = async () => {
            try {
                const params: any = {};
                if (selectedThemes && selectedThemes.length > 0) params.theme_ids = selectedThemes.join(',');
                if (selectedStatuses && selectedStatuses.length > 0) params.statuses = selectedStatuses.join(',');
                if (selectedQuizzes && selectedQuizzes.length > 0) params.quiz_names = selectedQuizzes.join(',');
                const res = await axios.get('/admin/dashboard/paper-attempts', { params });
                setData(
                    res.data.papers.map((p: any) => ({
                        ...p,
                        passed: p.num_passed,
                        failed: p.num_failed,
                        "Avg Attempts": p.avg_attempts_per_student,
                    }))
                );
            } catch (e) {
                setData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [selectedThemes, selectedStatuses, selectedQuizzes]);
    if (loading) return <Typography>Loading...</Typography>;
    if (!data.length) return <Typography>No data available.</Typography>;
    return (
        <ResponsiveContainer width="100%" height={380}>
            <BarChart
                data={data}
                margin={{ top: 40, right: 40, left: 20, bottom: 40 }}
                barCategoryGap={40}
            >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="title" tick={{ fontSize: 13 }} interval={0} angle={-30} textAnchor="end" height={80} />
                <YAxis allowDecimals={false} label={{ value: 'Students', angle: -90, position: 'insideLeft', fontSize: 11 }} tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" hide={true} />
                <Tooltip content={({ active, payload, label }) => {
                    if (active && payload && payload.length && payload[0].payload) {
                        const d = payload[0].payload;
                        const total = d.passed + d.failed;
                        return (
                            <Box sx={{ bgcolor: 'background.paper', p: 1, borderRadius: 1, boxShadow: 2 }}>
                                <Typography variant="subtitle2" gutterBottom>{label}</Typography>
                                <Typography variant="body2" color="success.main">
                                    Passed: {d.passed} ({total ? Math.round((d.passed/total)*100) : 0}%)
                                </Typography>
                                <Typography variant="body2" color="error.main">
                                    Failed: {d.failed} ({total ? Math.round((d.failed/total)*100) : 0}%)
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Avg Attempts: {d["Avg Attempts"]}
                                </Typography>
                            </Box>
                        );
                    }
                    return null;
                }} />
                <Legend
    layout="vertical"
    align="right"
    verticalAlign="middle"
    wrapperStyle={{ right: 0, top: 60, width: 120, position: 'absolute', fontSize: 12 }}
/>
                <Bar dataKey="passed" stackId="a" name="Passed" fill={theme.palette.success.main} />
                <Bar dataKey="failed" stackId="a" name="Failed" fill={theme.palette.error.main} />
                {/* Render avgAttempts chip after bars for higher stacking order */}
                <Bar dataKey="Avg Attempts" fillOpacity={0} isAnimationActive={false}>
                    <LabelList
                        dataKey="Avg Attempts"
                        position="top"
                        content={({ x, y, width, value }: any) => (
                            <g>
                                <circle cx={x + width / 2} cy={y - 22} r={18} fill={theme.palette.primary.light} />
                                <text x={x + width / 2} y={y - 18} textAnchor="middle" fontSize={14} fontWeight="bold" alignmentBaseline="middle" fill={theme.palette.primary.contrastText}>
                                    {typeof value === 'number' ? value.toFixed(1) : value}
                                </text>
                            </g>
                        )}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

import { Sankey } from 'recharts';

// Custom tooltip for Sankey chart
const CustomSankeyTooltip = ({ active, payload, nodes, realLinks }: any) => {
    if (active && payload && payload.length && payload[0].payload) {
        const link = payload[0].payload;
        // Always get the real value from the original data, not visualLinks
        const match = realLinks.find((l: any) => l.source === link.source && l.target === link.target);
        const value = match ? match.value : link.value;
        const sourceName = nodes[link.source]?.name || '';
        const targetName = nodes[link.target]?.name || '';
        // Compute percent relative to the true source node (sum all links from the source node in the original data)
        let sourceTotal = 0;
        if (typeof link.source === 'number') {
            sourceTotal = realLinks.filter((l: any) => l.source === link.source).reduce((sum: number, l: any) => sum + l.value, 0);
        }
        const percent = sourceTotal ? ((value / sourceTotal) * 100).toFixed(1) : '0.0';
        return (
            <Box sx={{ bgcolor: 'background.paper', p: 1, borderRadius: 1, boxShadow: 2 }}>
                <Typography variant="body2" color="text.primary">
                    <b>{sourceName}</b> → <b>{targetName}</b>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Count: <b>{value}</b> ({percent}%)
                </Typography>
            </Box>
        );
    }
    return null;
};

// SankeyChart Component
const SankeyChart: React.FC = () => {
    const theme = useTheme();
    const [data, setData] = React.useState<any | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await axios.get('/admin/dashboard/email-sankey');
                const d = res.data;
                // Build nodes and links for Recharts Sankey
                // Nodes: 0:All Students, 1:Valid Email, 2:Subscribed, 3:Unsubscribed, 4:Invalid Email
                // Ensure all values are valid numbers
                const total = typeof d.total === 'number' && !isNaN(d.total) ? d.total : 0;
                const valid_email = typeof d.valid_email === 'number' && !isNaN(d.valid_email) ? d.valid_email : 0;
                const invalid_email = typeof d.invalid_email === 'number' && !isNaN(d.invalid_email) ? d.invalid_email : 0;
                // Use backend value for subscribed, and always calculate unsubscribed as valid_email - subscribed
                const subscribed = typeof d.subscribed === 'number' && !isNaN(d.subscribed) ? d.subscribed : 0;
                const unsubscribed = Math.max(valid_email - subscribed, 0);
                const nodes = [
                    { name: 'All Students', color: theme.palette.primary.main },
                    { name: 'Valid Email', color: theme.palette.success.main },
                    { name: 'Subscribed', color: theme.palette.secondary.main },
                    { name: 'Unsubscribed', color: theme.palette.warning.main },
                    { name: 'Invalid Email', color: theme.palette.error.main }
                ];
                // For visualization: scale down links but keep real values for tooltips/labels
                const maxBranchWidth = 80; // px (tweak as needed)
                const maxValue = Math.max(valid_email, invalid_email, subscribed, unsubscribed, 1);
                const realLinks = [
                    { source: 0, target: 1, value: valid_email, color: theme.palette.success.main },
                    { source: 1, target: 2, value: subscribed, color: theme.palette.secondary.main },
                    { source: 1, target: 3, value: unsubscribed, color: theme.palette.warning.main },
                    { source: 0, target: 4, value: invalid_email, color: theme.palette.error.main }
                ].filter(link => link.value > 0);
                const visualLinks = realLinks.map(l => ({ ...l, visualValue: Math.max(10, l.value / maxValue * maxBranchWidth) }));
                setData({ nodes, links: visualLinks, realLinks, total, valid_email, invalid_email, subscribed });
            } catch (e: any) {
                setError('Failed to load Sankey data');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    if (loading) return <Typography color="text.secondary">Loading...</Typography>;
    if (error) return <Typography color="error">{error}</Typography>;
    if (!data || !data.nodes || !data.links || data.total === 0 || (data.links.every((l:any) => !l.value))) {
        return <Typography color="text.secondary">No Sankey data to display.</Typography>;
    }

    // Custom node renderer as a React component for Sankey
    const CustomNode = (props: any) => {
        const { x, y, width, height, index, payload } = props;
        let value = 0;
        if (index === 0) value = data.total;
        if (index === 1) value = data.valid_email;
        if (index === 2) value = data.subscribed;
        if (index === 3) value = data.valid_email - data.subscribed; // Unsubscribed
        if (index === 4) value = data.invalid_email; // Invalid Email
        const percent = index === 0 ? 100 : data.total ? ((value / data.total) * 100).toFixed(1) : '0.0';
        // Use a dark font color for labels for visibility
        const labelColor = theme.palette.text.primary || '#222';
        // For 'All Students', always center the label inside its rectangle
        // Leftmost node (All Students): align left, add padding
        if (index === 0) {
            return (
                <g>
                    <rect x={x} y={y} width={width} height={height} fill={payload.color} stroke={theme.palette.background.paper} rx={6} />
                    <text x={x + 10} y={y + height / 2 - 4} textAnchor="start" fill={labelColor} fontSize={13}>
    {payload.name}
</text>
<text x={x + 10} y={y + height / 2 + 14} textAnchor="start" fill={labelColor} fontSize={11}>
    {value} ({percent}%)
</text>
                </g>
            );
        }
        // Rightmost nodes (leaf nodes: Subscribed, Unsubscribed, Invalid Email): align right, add padding
        const isRightmost = index === 2 || index === 3 || index === 4; // Subscribed, Unsubscribed, Invalid Email
        if (isRightmost) {
            return (
                <g>
                    <rect x={x} y={y} width={width} height={height} fill={payload.color} stroke={theme.palette.background.paper} rx={6} />
                    <text x={x + width - 10} y={y + height / 2 - 4} textAnchor="end" fill={labelColor} fontSize={13}>
    {payload.name}
</text>
<text x={x + width - 10} y={y + height / 2 + 14} textAnchor="end" fill={labelColor} fontSize={11}>
    {value} ({percent}%)
</text>
                </g>
            );
        }
        // All other nodes: center
        return (
            <g>
                <rect x={x} y={y} width={width} height={height} fill={payload.color} stroke={theme.palette.background.paper} rx={6} />
                <text x={x + width / 2} y={y + height / 2 - 4} textAnchor="middle" fill={labelColor} fontSize={13}>
    {payload.name}
</text>
<text x={x + width / 2} y={y + height / 2 + 14} textAnchor="middle" fill={labelColor} fontSize={11}>
    {value} ({percent}%)
</text>
            </g>
        );
    };



    // Legend data
    const legendData = [
        { label: 'All Students', color: theme.palette.primary.main },
        { label: 'Valid Email', color: theme.palette.success.main },
        { label: 'Invalid Email', color: theme.palette.error.main },
        { label: 'Subscribed', color: theme.palette.secondary.main }
    ];

    return (
        <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>

            <ResponsiveContainer width="100%" height="90%">
                <Sankey
                    data={{...data, links: data.links.map((l: any) => ({...l, value: l.visualValue}))}}
                    nodePadding={40}
                    nodeWidth={28}
                    linkCurvature={0.5}
                    margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    node={<CustomNode />}
                    link={{
                        style: { opacity: 0.7 }
                    }}
                >
                                    </Sankey>
            </ResponsiveContainer>
        </Box>
    );
};

// --------------------

export default Dashboard;
